import { Button } from '@/components/Button';
import { LineSeparator } from '@/components/LineSeparator';
import { Loading } from '@/components/Loading';
import { Modal } from '@/components/Modal';
import { SelectInput } from '@/components/SelectInput';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { useCardStore } from '@/stores/useCardStore';
import i18n from '@/translations/config';
import { Card } from '@/types/card';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

export const UpdateCardSettings = () => {
  const { card, updateCard, deleteCard } = useCardStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  if (!card) return <Loading />;

  const handleSubmit = async (
    values: Card,
    { setSubmitting, setErrors }: FormikHelpers<Card>,
  ) => {
    try {
      const updatedCard = await updateCard(card.slug, values);
      navigate(`/${i18n.language}/dashboard/cards/${updatedCard.data.slug}`);
    } catch (error) {
      handleError(error, setErrors, navigate);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteCard(card.slug);
      setShowDeleteModal(false);
      navigate(`/${i18n.language}/dashboard`);
    } catch (error) {
      setIsDeleting(false);
    }
  };

  const validationSchema = yup.object().shape({
    slug: yup
      .string()
      .min(5)
      .max(255)
      .required(t('common:validation.required')),
  });

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Card Status Section */}
      <motion.section variants={itemVariants}>
        <motion.div variants={itemVariants} className="my-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('updateCard:settings.status.title')}
          </h2>
          <Text className="mt-1 text-sm text-gray-500">
            {t('updateCard:settings.status.description')}
          </Text>
        </motion.div>

        <Formik
          enableReinitialize
          initialValues={card}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, handleSubmit, isSubmitting }) => (
            <Form onSubmit={handleSubmit} className="space-y-4">
              <motion.div variants={itemVariants} className="mb-2 space-y-4">
                <TextInput
                  label={t('common:fields.slug')}
                  name="slug"
                  type="text"
                  onChange={handleChange}
                  containerStyle="w-full md:w-[250px]"
                />
              </motion.div>

              <motion.div variants={itemVariants} className="space-y-4">
                <SelectInput
                  label={t('common:fields.status')}
                  name="is_active"
                  options={[
                    { label: t('common:fields.active'), value: 1 },
                    { label: t('common:fields.inactive'), value: 0 },
                  ]}
                  defaultValue={values.is_active}
                  onChange={handleChange}
                  containerStyle="w-full md:w-[250px]"
                />

                <div>
                  <Button
                    type="submit"
                    className="w-full bg-secondary text-white"
                    disabled={isSubmitting}
                  >
                    {t('common:update')}
                  </Button>
                </div>
              </motion.div>
            </Form>
          )}
        </Formik>
      </motion.section>

      <LineSeparator />

      {/* Danger Zone Section */}
      <motion.section variants={itemVariants}>
        <motion.div variants={itemVariants} className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('updateCard:settings.delete.title')}
          </h2>
          <Text className="mt-1 text-sm text-gray-500">
            {t('updateCard:settings.delete.description')}
          </Text>
        </motion.div>

        <div className="flex justify-start">
          <Button
            className="w-full bg-danger text-white hover:bg-danger sm:w-auto"
            onClick={() => setShowDeleteModal(true)}
            disabled={isDeleting}
          >
            {t('common:delete')}
          </Button>
        </div>
      </motion.section>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('updateCard:settings.delete.title')}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          <Text className="text-gray-600">
            {t('updateCard:settings.delete.warning')}
          </Text>
          <motion.div
            className="flex justify-end space-x-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <Button
                className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                {t('common:cancel')}
              </Button>
            </motion.div>

            <Button
              className="bg-danger text-white"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {t('common:delete')}
            </Button>
          </motion.div>
        </motion.div>
      </Modal>
    </motion.div>
  );
};
